// src/components/Admin/SnackableCourse/renderers/ContentBlockRenderer.tsx

import React from 'react';
import DOMPurify from 'dompurify';
import {
  Box,
  Typography,
  Alert,
  Paper,
  List,
  ListItem,
} from '@mui/material';
import { SnackableContentBlock } from '../../../../types/moodleTypes';

interface Props {
  block: SnackableContentBlock;
  index: number;
}

export const ContentBlockRenderer: React.FC<Props> = ({ block, index }) => {
  const blockContent = block.text || block.content || '';

  switch (block.type) {
    case 'heading': {
      const level = block.level || 3;
      const variant = level === 1 ? 'h4' : level === 2 ? 'h5' : 'h6';
      return (
        <Typography
          key={index}
          variant={variant}
          gutterBottom
          sx={{
            mt: 2,
            fontWeight: 600,
            color: block.color || 'text.primary',
          }}
        >
          {blockContent}
        </Typography>
      );
    }

    case 'paragraph':
      return (
        <Typography
          key={index}
          variant="body1"
          paragraph
          sx={{ mb: 2, lineHeight: 1.7 }}
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(blockContent) }}
        />
      );

    case 'list':
      return (
        <Box key={index} sx={{ mb: 2 }}>
          {block.ordered ? (
            <List component="ol" sx={{ listStyleType: 'decimal', pl: 4 }}>
              {block.items?.map((item, idx) => (
                <ListItem key={idx} sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography
                    variant="body2"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item) }}
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <List component="ul" sx={{ listStyleType: 'disc', pl: 4 }}>
              {block.items?.map((item, idx) => (
                <ListItem key={idx} sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography
                    variant="body2"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item) }}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      );

    case 'callout':
      return (
        <Alert
          key={index}
          severity={
            block.style === 'success'
              ? 'success'
              : block.style === 'warning'
                ? 'warning'
                : block.style === 'error'
                  ? 'error'
                  : 'info'
          }
          sx={{ mb: 2 }}
        >
          {block.title && (
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              {block.title}
            </Typography>
          )}
          <Typography
            variant="body2"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(blockContent) }}
          />
        </Alert>
      );

    case 'card':
      return (
        <Paper
          key={index}
          sx={{
            mb: 2,
            p: 2,
            bgcolor: block.styles?.background || 'grey.100',
            borderRadius: block.styles?.border_radius || 1,
          }}
        >
          {block.title && (
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              {block.title}
            </Typography>
          )}
          <Typography
            variant="body2"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(blockContent) }}
          />
        </Paper>
      );

    case 'note':
    case 'warning':
      return (
        <Alert key={index} severity="warning" sx={{ mb: 2 }}>
          <Typography
            variant="body2"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(blockContent) }}
          />
        </Alert>
      );

    case 'code':
      return (
        <Paper
          key={index}
          variant="outlined"
          sx={{
            mb: 2,
            p: 2,
            bgcolor: 'grey.100',
            fontFamily: 'monospace',
            fontSize: '0.875rem',
            overflow: 'auto',
          }}
        >
          <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{blockContent}</pre>
        </Paper>
      );

    case 'table':
      return (
        <Paper key={index} variant="outlined" sx={{ mb: 2, overflow: 'auto' }}>
          <table
            style={{
              width: block.styles?.width || '100%',
              borderCollapse: 'collapse',
            }}
          >
            {block.headers && (
              <thead>
              <tr>
                {block.headers.map((header, idx) => (
                  <th
                    key={idx}
                    style={{
                      padding: '12px',
                      borderBottom: '2px solid #ddd',
                      textAlign: 'left',
                      fontWeight: 600,
                      backgroundColor: '#f5f5f5',
                      fontSize: block.styles?.font_size || '14px',
                    }}
                  >
                    {header}
                  </th>
                ))}
              </tr>
              </thead>
            )}
            <tbody>
            {block.rows?.map((row, rowIdx) => (
              <tr key={rowIdx}>
                {row.map((cell, cellIdx) => (
                  <td
                    key={cellIdx}
                    style={{
                      padding: '12px',
                      borderBottom: '1px solid #ddd',
                      fontSize: block.styles?.font_size || '14px',
                    }}
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(cell) }}
                  />
                ))}
              </tr>
            ))}
            </tbody>
          </table>
        </Paper>
      );

    default:
      return (
        <Typography key={index} variant="body2" sx={{ mb: 1 }}>
          {blockContent}
        </Typography>
      );
  }
};